import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuditApiService } from '../../camera-audit-live/core/audit-api.service';
import {
	CameraNote,
	cameraNote,
	describeMachineRestock,
	formatVisitTime,
	groupRestockVisits,
	readShowCameraReadings,
	RESTOCK_RANGES,
	RestockComparison,
	RestockComparisonResponse,
	RestockRange,
	RestockVisit,
	unloggedRestockText,
	writeShowCameraReadings
} from '../../../../core/machine-camera/_services/machine-camera.util';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { MachineRouteService } from '../machine-route.service';

// A machine's restock history, one entry per stop: what the operator logged, with the camera's
// view alongside where the machine has one. Super admins only.
@Component({
	selector: 'kt-machine-restocks',
	templateUrl: './restocks.component.html',
	styleUrls: ['./restocks.component.scss']
})
export class RestocksComponent implements OnInit, OnDestroy {
	readonly ranges = RESTOCK_RANGES;
	readonly formatVisitTime = formatVisitTime;
	readonly describeMachineRestock = describeMachineRestock;
	readonly unloggedRestockText = unloggedRestockText;

	range: RestockRange = '14d';
	machineId = '';
	isLoading = false;
	loadError = '';
	data: RestockComparisonResponse = null;
	visits: RestockVisit[] = [];
	showCamera = readShowCameraReadings();

	private loadSubscription: Subscription = null;

	constructor(
		private route: ActivatedRoute,
		private machineRouteService: MachineRouteService,
		private cameraApi: AuditApiService,
		private cdr: ChangeDetectorRef,
		private mixpanelService: MixpanelService
	) {}

	ngOnInit(): void {
		const machine = this.machineRouteService.getMachine(this.route);
		if (!machine) {
			this.loadError = 'Open this page from a machine.';
			return;
		}
		this.machineId = String(machine.machine_id);
		this.mixpanelService.track(this.mixpanelService.PAGE_VIEW, {
			machine_id: machine.machine_id,
			machine_name: machine.machine_name,
			machine_serial_no: machine.serial_no
		});
		this.load();
	}

	ngOnDestroy(): void {
		if (this.loadSubscription) {
			this.loadSubscription.unsubscribe();
		}
	}

	onRangeChange(range: RestockRange): void {
		if (range === this.range) {
			return;
		}
		this.range = range;
		this.load();
	}

	onShowCameraChange(show: boolean): void {
		this.showCamera = show;
		writeShowCameraReadings(show);
		this.visits = this.buildVisits();
		this.cdr.detectChanges();
	}

	load(): void {
		if (this.loadSubscription) {
			this.loadSubscription.unsubscribe();
		}
		this.isLoading = true;
		this.loadError = '';
		this.loadSubscription = this.cameraApi.getMachineCameraRestocks(this.machineId, this.range).subscribe(data => {
			this.data = data;
			this.visits = this.buildVisits();
			this.isLoading = false;
			this.cdr.detectChanges();
		}, () => {
			this.data = null;
			this.visits = [];
			this.isLoading = false;
			this.loadError = 'Restocks could not be loaded. Try again in a moment.';
			this.cdr.detectChanges();
		});
	}

	get cameraShown(): boolean {
		return Boolean(this.data && this.data.has_camera && !this.data.camera_unavailable && this.showCamera);
	}

	rangeLabel(): string {
		const range = this.ranges.find(option => option.value === this.range);
		return range ? range.label : '';
	}

	canisterName(restock: RestockComparison): string {
		return restock.name || `Slot ${restock.slot_no}`;
	}

	canisterCount(visit: RestockVisit): string {
		const count = visit.restocks.length;
		return `${count} ${count === 1 ? 'canister' : 'canisters'}`;
	}

	note(restock: RestockComparison): CameraNote | null {
		return this.cameraShown ? cameraNote(restock) : null;
	}

	gramsTooltip(grams: number | null): string {
		return grams === null || grams === undefined ? '' : `${Math.round(grams).toLocaleString()} g added`;
	}

	trackVisit(index: number, visit: RestockVisit): string {
		return visit.at;
	}

	// Camera-only restocks are shown only alongside the camera's other readings.
	private buildVisits(): RestockVisit[] {
		if (!this.data) {
			return [];
		}
		const restocks = this.cameraShown
			? this.data.restocks
			: this.data.restocks.filter(restock => restock.machine);
		return groupRestockVisits(restocks);
	}
}
